/* eslint-disable @typescript-eslint/no-explicit-any */

// deps
import React, { useContext, useMemo } from "react";

// internal-deps
import { getCloudinaryUrl } from "@swiggy-private/js-utils";
import type { NRErrorInfo } from "@swiggy-private/js-utils";
import type { GetSwiggyHeadersResponsePayload, NativeBridge } from "@swiggy-private/native-bridge";
import { PLWidgetController, WidgetControllerContext } from "@swiggy-private/dls-react-mxn/dist";
import type { IPLWidgetControllerCards } from "@swiggy-private/dls-react-mxn/dist/WidgetController/PLWidgetController/type";

// styles
import { ResponseContext, ScreenNames, IApiConfigs } from "@swiggy-private/portal-dineout-context";
import { hasCTAWidget } from "./helpers";
import { BottomWidget, Container, FlexContainer, OverLayError } from "./styles";
import { ErrorPageWrapper } from "../BillSummary/styles";
// constants
import { EMPTY_CART_SCREEN } from "../constants/cart";

// components
import ErrorComponent from "../ErrorComponent/ErrorComponent";
import { useAnalytics } from "../hooks";
import { PLWidgetRenderer } from "../PLWidgetRenderer";

interface ICartProps {
    cards: IPLWidgetControllerCards[];
    popupConfigs?: IApiConfigs;
    sdk: NativeBridge;
    headers: GetSwiggyHeadersResponsePayload;
    clickAnalytics?: (clickObject: Record<string, any>) => void;
    impressionAnalytics?: (clickObject: Record<string, any>) => void;
    genericOnclick: (_?: any, extraParams?: Record<string, any>) => Promise<void>;
    onInvalidWidget: (widgetName?: string) => void;
    onWidgetError: (error: NRErrorInfo, widgetName?: string) => void;
    isCartEmpty?: boolean;
    emptyCartCtaHandler: VoidFunction;
    onEmptyPage: VoidFunction;
    /** Screen name for analytics */
    screenName: ScreenNames;
}
const Cart = ({
    cards,
    popupConfigs,
    sdk,
    headers,
    impressionAnalytics: defaultImpressionAnalytics,
    clickAnalytics: defaultClickAnalytics,
    genericOnclick,
    isCartEmpty,
    emptyCartCtaHandler,
    onInvalidWidget,
    onWidgetError,
    onEmptyPage,
    screenName,
}: ICartProps) => {
    const { state } = useContext(ResponseContext);
    const { impressionAnalytics, clickAnalytics } = useAnalytics({
        screenName: ScreenNames.CART,
        defaultImpressionAnalytics,
        defaultClickAnalytics,
    });

    const contextValue = useMemo(() => {
        return {
            sdk,
            impressionAnalytics,
            clickAnalytics,
            headers,
            clickHandler: genericOnclick,
        };
    }, [clickAnalytics, genericOnclick, headers, impressionAnalytics, sdk]);

    const showBottomWidget = useMemo(() => hasCTAWidget(cards), [cards]);

    const OverlayWrapper = useMemo(
        () => (state.placeOrderFailure?.isFullScreenError ? OverLayError : React.Fragment),
        [state.placeOrderFailure?.isFullScreenError]
    );

    if (isCartEmpty) {
        return (
            <ErrorPageWrapper>
                <ErrorComponent
                    onCtaClick={emptyCartCtaHandler}
                    {...EMPTY_CART_SCREEN}
                    imgSrc={getCloudinaryUrl(EMPTY_CART_SCREEN.img, {
                        width: 220 * 2,
                        height: 200 * 2,
                    })}
                    showFullPage={false}
                />
            </ErrorPageWrapper>
        );
    }

    return (
        <Container>
            <FlexContainer>
                <WidgetControllerContext.Provider value={contextValue}>
                    <PLWidgetRenderer
                        cards={cards}
                        popupConfigs={popupConfigs}
                        screenName={screenName}
                        onInvalidWidget={onInvalidWidget}
                        onWidgetError={onWidgetError}
                        onEmptyPage={onEmptyPage}
                    />
                    {/* Overlay for error */}
                    {state.placeOrderFailure.error && (
                        <OverlayWrapper>
                            <PLWidgetController
                                onInvalidWidget={onInvalidWidget}
                                onWidgetError={onWidgetError}
                                onEmptyPage={onEmptyPage}
                                cards={state.placeOrderFailure.data?.success?.cards || []}
                            />
                        </OverlayWrapper>
                    )}
                </WidgetControllerContext.Provider>
                {showBottomWidget ? <BottomWidget /> : null}
            </FlexContainer>
        </Container>
    );
};

export default React.memo(Cart);
